/* SOCT Store Theme JS — N&D Sports
 * Global utilities only. Section-specific JS lives inline in each section.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════
     Utilities
  ══════════════════════════════════════════ */

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || window.rwMoneyFormat || '${{amount}}').replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.floor(cents / 100));
  }

  /* Expose globally for section scripts */
  window.rwUtils = { debounce, formatMoney };

  /* ══════════════════════════════════════════
     Header height → CSS custom property
     Used by sticky elements: --header-h
  ══════════════════════════════════════════ */

  function updateHeaderHeight() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const h = header.offsetHeight;
    document.documentElement.style.setProperty('--header-h', h + 'px');
  }

  const ro = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(debounce(updateHeaderHeight, 80))
    : null;

  const siteHeader = document.querySelector('.site-header');
  if (siteHeader) {
    updateHeaderHeight();
    ro?.observe(siteHeader);
  }

  /* ══════════════════════════════════════════
     Header scroll state
     Adds body.is-scrolled for compact styles
  ══════════════════════════════════════════ */

  let lastScrollY = 0;
  let ticking = false;

  function onScroll() {
    const y = window.scrollY;
    document.body.classList.toggle('is-scrolled', y > 60);
    lastScrollY = y;
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(onScroll);
      ticking = true;
    }
  }, { passive: true });

  /* ══════════════════════════════════════════
     Back-to-top button (JS-injected)
  ══════════════════════════════════════════ */

  const btt = document.createElement('button');
  btt.className = 'back-to-top';
  btt.setAttribute('type', 'button');
  btt.setAttribute('aria-label', 'Back to top');
  btt.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="20" height="20" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>';
  document.body.appendChild(btt);

  btt.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  window.addEventListener('scroll', debounce(() => {
    btt.classList.toggle('is-visible', window.scrollY > 600);
  }, 80), { passive: true });

  /* ══════════════════════════════════════════
     Announcement bar dismiss
  ══════════════════════════════════════════ */

  const announceBar = document.querySelector('[data-announcement-bar]');
  if (announceBar) {
    if (sessionStorage.getItem('rw-announcement-dismissed')) {
      announceBar.hidden = true;
    }
    announceBar.querySelector('[data-announcement-close]')?.addEventListener('click', () => {
      announceBar.hidden = true;
      sessionStorage.setItem('rw-announcement-dismissed', '1');
    });
  }

  /* ══════════════════════════════════════════
     Anchor smooth-scroll (for in-page links)
  ══════════════════════════════════════════ */

  document.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h') || '0', 10);
    const top = target.getBoundingClientRect().top + window.scrollY - headerH - 16;
    window.scrollTo({ top, behavior: 'smooth' });
    /* Move focus for accessibility */
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* ══════════════════════════════════════════
     Shopify theme editor — section refresh
  ══════════════════════════════════════════ */

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => {
      updateHeaderHeight();
    });
    document.addEventListener('shopify:section:reorder', () => {
      updateHeaderHeight();
    });
  }

  /* ══════════════════════════════════════════
     Cart API helpers (shared across sections)
  ══════════════════════════════════════════ */

  window.rwCart = {
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },
    async add(formData) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add to cart');
      }
      return res.json();
    },
    async change(id, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      return res.json();
    },
    async update(updates) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return res.json();
    }
  };

})();
